import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Send, Upload, Download, Trash2, Wifi, WifiOff, FileText, FileCheck, CheckCircle, RotateCcw, BookOpen, Ruler, ImageIcon, Sun, Cloud, CloudRain, Pencil } from 'lucide-react';

export default function Projekte() {
  const { API, BACKEND_URL, token, user, isAdmin, canWrite } = useAuth();
  const canEditProjects = canWrite('projekte');
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', customer_id: '', status: 'aktiv', start_date: '', end_date: '', planned_duration_days: '', members: [] });
  const [angebote, setAngebote] = useState([]);
  const [selectedAngebot, setSelectedAngebot] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [docs, setDocs] = useState([]);
  const [diary, setDiary] = useState([]);
  const [diaryForm, setDiaryForm] = useState({ date: '', text: '', weather: '', workers: '' });
  const [diaryPhoto, setDiaryPhoto] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [measForm, setMeasForm] = useState({ room: '', items: [{ description: '', length: 0, width: 0, height: 0, quantity: 1, unit: 'm²', notes: '' }] });
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const diaryFileRef = useRef(null);

  const loadProjects = useCallback(async () => {
    const [p, c, a, u] = await Promise.all([
      axios.get(`${API}/projects`), axios.get(`${API}/customers`),
      axios.get(`${API}/financial-documents?doc_type=angebot`), axios.get(`${API}/users`)
    ]);
    setProjects(p.data);
    setCustomers(c.data);
    setAngebote(a.data);
    setAllUsers(u.data);
  }, [API]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (!selected || activeTab !== 'chat') return;
    axios.get(`${API}/messages/${selected.id}`).then(r => setMessages(r.data)).catch(() => {});
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${selected.id}?token=${token}`);
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
    };
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    wsRef.current = ws;
    return () => { ws.close(); setWsConnected(false); };
  }, [selected, activeTab, API, BACKEND_URL, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!selected || activeTab !== 'dokumente') return;
    axios.get(`${API}/documents?project_id=${selected.id}`).then(r => setDocs(r.data)).catch(() => {});
  }, [selected, activeTab, API]);

  useEffect(() => {
    if (!selected || activeTab !== 'tagebuch') return;
    axios.get(`${API}/projects/${selected.id}/diary`).then(r => setDiary(r.data)).catch(() => {});
  }, [selected, activeTab, API]);

  useEffect(() => {
    if (!selected || activeTab !== 'aufmass') return;
    axios.get(`${API}/measurements?project_id=${selected.id}`).then(r => setMeasurements(r.data)).catch(() => {});
  }, [selected, activeTab, API]);

  const sendMessage = () => {
    if (!newMsg.trim() || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ content: newMsg }));
    setNewMsg('');
  };

  const resetForm = () => setForm({ name: '', description: '', customer_id: '', status: 'aktiv', start_date: '', end_date: '', planned_duration_days: '', members: [] });

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (selectedAngebot) payload.angebot_id = selectedAngebot.id;
      if (payload.planned_duration_days) payload.planned_duration_days = parseInt(payload.planned_duration_days) || 0;

      let res;
      if (editingProject) {
        res = await axios.put(`${API}/projects/${editingProject.id}`, payload);
        setProjects(prev => prev.map(p => p.id === editingProject.id ? res.data : p));
        setSelected(res.data);
        toast.success('Projekt aktualisiert');
      } else {
        res = await axios.post(`${API}/projects`, payload);
        setProjects(prev => [...prev, res.data]);
        toast.success(selectedAngebot ? 'Projekt aus Angebot erstellt' : 'Projekt erstellt');
      }
      setShowCreate(false);
      setEditingProject(null);
      resetForm();
      setSelectedAngebot(null);
    } catch { toast.error('Fehler'); }
  };

  const openEditProject = (p) => {
    setEditingProject(p);
    setSelectedAngebot(null);
    setForm({
      name: p.name || '', description: p.description || '', customer_id: p.customer_id || '',
      status: p.status || 'aktiv', start_date: p.start_date || '', end_date: p.end_date || '',
      planned_duration_days: p.planned_duration_days || '', members: p.members || [],
    });
    setShowCreate(true);
  };

  const applyAngebot = (angebot) => {
    if (!angebot) { setSelectedAngebot(null); return; }
    setSelectedAngebot(angebot);
    const customer = customers.find(c => c.id === angebot.customer_id);
    const itemLines = (angebot.items || []).map((it, i) =>
      `${i + 1}. ${it.description} (${it.quantity} ${it.unit} x ${it.unit_price.toFixed(2)} EUR)`
    ).join('\n');
    setForm({
      name: `${customer?.name || 'Kunde'} — ${angebot.doc_number}`,
      description: `Aus Angebot ${angebot.doc_number} erstellt\n\nPositionen:\n${itemLines}\n\nNetto: ${angebot.subtotal?.toFixed(2)} EUR | Brutto: ${angebot.total?.toFixed(2)} EUR${angebot.notes ? '\n\nAnmerkungen: ' + angebot.notes : ''}`,
      customer_id: angebot.customer_id || '',
      status: 'aktiv',
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', selected.id);
    try {
      const res = await axios.post(`${API}/documents/upload`, fd);
      setDocs(prev => [...prev, res.data]);
      toast.success('Dokument hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
  };

  const handleDeleteDoc = async (id) => {
    await axios.delete(`${API}/documents/${id}`);
    setDocs(prev => prev.filter(d => d.id !== id));
    toast.success('Gelöscht');
  };

  const handleDeleteProject = async (id) => {
    await axios.delete(`${API}/projects/${id}`);
    setProjects(prev => prev.filter(p => p.id !== id));
    setSelected(null);
    toast.success('Projekt gelöscht');
  };

  const handleDiarySubmit = async () => {
    const fd = new FormData();
    fd.append('date', diaryForm.date || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
    fd.append('text', diaryForm.text);
    fd.append('weather', diaryForm.weather);
    fd.append('workers', diaryForm.workers);
    if (diaryPhoto) fd.append('file', diaryPhoto);
    try {
      const res = await axios.post(`${API}/projects/${selected.id}/diary`, fd);
      setDiary(prev => [res.data, ...prev]);
      setDiaryForm({ date: '', text: '', weather: '', workers: '' });
      setDiaryPhoto(null);
      toast.success('Eintrag gespeichert');
    } catch { toast.error('Fehler'); }
  };

  const handleMeasSubmit = async () => {
    try {
      const res = await axios.post(`${API}/measurements`, { project_id: selected.id, room: measForm.room, items: measForm.items });
      setMeasurements(prev => [res.data, ...prev]);
      setMeasForm({ room: '', items: [{ description: '', length: 0, width: 0, height: 0, quantity: 1, unit: 'm²', notes: '' }] });
      toast.success('Aufmaß gespeichert');
    } catch { toast.error('Fehler'); }
  };

  const handleToggleStatus = async (project, newStatus) => {
    try {
      const res = await axios.put(`${API}/projects/${project.id}`, { status: newStatus });
      setProjects(prev => prev.map(p => p.id === project.id ? res.data : p));
      setSelected(res.data);
      toast.success(newStatus === 'abgeschlossen' ? 'Projekt abgeschlossen' : 'Projekt reaktiviert');
    } catch { toast.error('Fehler'); }
  };

  const [projectTab, setProjectTab] = useState('aktiv');
  const activeProjects = projects.filter(p => p.status !== 'abgeschlossen');
  const closedProjects = projects.filter(p => p.status === 'abgeschlossen');

  const statusColors = { aktiv: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20', abgeschlossen: 'bg-blue-400/10 text-blue-400 border-blue-400/20', pausiert: 'bg-amber-400/10 text-amber-400 border-amber-400/20' };

  if (selected) {
    return (
      <div className="space-y-4 animate-fade-in" data-testid="project-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setSelected(null); setActiveTab('info'); }} className="text-slate-400 hover:text-white" data-testid="back-to-projects">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-white">{selected.name}</h1>
          <Badge className={`${statusColors[selected.status] || ''} rounded-sm`}>{selected.status}</Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="info" data-testid="tab-info" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Info</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Chat</TabsTrigger>
            <TabsTrigger value="dokumente" data-testid="tab-dokumente" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Dokumente</TabsTrigger>
            <TabsTrigger value="tagebuch" data-testid="tab-tagebuch" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Tagebuch</TabsTrigger>
            <TabsTrigger value="aufmass" data-testid="tab-aufmass" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Aufmaß</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-3">
              <p className="text-slate-300">{selected.description || 'Keine Beschreibung'}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Kunde:</span><span className="text-slate-300 ml-2">{customers.find(c => c.id === selected.customer_id)?.name || '—'}</span></div>
                <div><span className="text-slate-500">Status:</span><span className="text-slate-300 ml-2 capitalize">{selected.status}</span></div>
                <div><span className="text-slate-500">Start:</span><span className="text-slate-300 ml-2 font-mono">{selected.start_date || '—'}</span></div>
                <div><span className="text-slate-500">Ende:</span><span className="text-slate-300 ml-2 font-mono">{selected.end_date || '—'}</span></div>
                {selected.planned_duration_days && <div><span className="text-slate-500">Geplante Dauer:</span><span className="text-slate-300 ml-2 font-mono">{selected.planned_duration_days} Tage</span></div>}
              </div>
              {canEditProjects && (
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" onClick={() => openEditProject(selected)} data-testid="edit-project"
                  className="bg-amber-500/10 text-amber-400 border border-amber-400/20 hover:bg-amber-500/20">
                  <Pencil className="w-4 h-4 mr-2" />Bearbeiten
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDeleteProject(selected.id)} data-testid="delete-project"
                  className="bg-rose-500/10 text-rose-400 border border-rose-400/20 hover:bg-rose-500/20">
                  <Trash2 className="w-4 h-4 mr-2" />Löschen
                </Button>
                {selected.status !== 'abgeschlossen' ? (
                  <Button size="sm" onClick={() => handleToggleStatus(selected, 'abgeschlossen')} data-testid="close-project"
                    className="bg-blue-500/10 text-blue-400 border border-blue-400/20 hover:bg-blue-500/20">
                    <CheckCircle className="w-4 h-4 mr-2" />Abschließen
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => handleToggleStatus(selected, 'aktiv')} data-testid="reopen-project"
                    className="bg-emerald-500/10 text-emerald-400 border border-emerald-400/20 hover:bg-emerald-500/20">
                    <RotateCcw className="w-4 h-4 mr-2" />Reaktivieren
                  </Button>
                )}
              </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="chat" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm flex flex-col" style={{ height: '500px' }} data-testid="project-chat">
              <div className="px-4 py-2 border-b border-slate-800/40 flex items-center gap-2">
                {wsConnected ? <><Wifi className="w-4 h-4 text-emerald-400" /><span className="text-xs text-emerald-400">Verbunden</span></> : <><WifiOff className="w-4 h-4 text-slate-500" /><span className="text-xs text-slate-500">Getrennt</span></>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 chat-scroll">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-3 py-2 ${m.user_id === user?.id ? 'bg-amber-500 text-slate-950 rounded-tl-sm rounded-bl-sm rounded-br-sm' : 'bg-slate-800 text-slate-200 rounded-tr-sm rounded-br-sm rounded-bl-sm'}`}>
                      {m.user_id !== user?.id && <p className="text-xs font-semibold mb-1 opacity-70">{m.user_name}</p>}
                      <p className="text-sm">{m.content}</p>
                      <p className="text-[10px] opacity-50 mt-1 font-mono">{new Date(m.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="p-3 border-t border-slate-800/40 flex gap-2">
                <Input data-testid="chat-input" value={newMsg} onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Nachricht schreiben..."
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 focus:border-amber-500" />
                <Button onClick={sendMessage} data-testid="chat-send" className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm shrink-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="dokumente" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Dokumente</h3>
                {canEditProjects && (
                  <>
                    <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-doc-button"
                      className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
                      <Upload className="w-4 h-4 mr-2" />Hochladen
                    </Button>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
                  </>
                )}
              </div>
              {docs.length ? (
                <div className="space-y-2">
                  {docs.map(d => (
                    <div key={d.id} className="flex items-center justify-between py-3 px-4 bg-slate-950/50 rounded-sm border border-slate-800/40" data-testid={`doc-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                        <div>
                          <p className="text-sm font-medium text-slate-300">{d.filename}</p>
                          <p className="text-xs text-slate-500">{(d.size / 1024).toFixed(1)} KB · {d.uploaded_by_name}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-amber-400"><Download className="w-4 h-4" /></Button>
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(d.id)} className="text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500">Noch keine Dokumente</p>}
            </div>
          </TabsContent>

          {/* Bautagebuch */}
          <TabsContent value="tagebuch" className="mt-4 space-y-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 space-y-3">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><BookOpen className="w-4 h-4 text-amber-500" />Neuer Eintrag</h3>
              <div className="grid grid-cols-3 gap-3">
                <Input type="date" value={diaryForm.date} onChange={e => setDiaryForm({...diaryForm, date: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200" />
                <select value={diaryForm.weather} onChange={e => setDiaryForm({...diaryForm, weather: e.target.value})}
                  className="bg-slate-950 border border-slate-800 text-slate-200 rounded-sm text-sm px-3">
                  <option value="">Wetter...</option><option value="sonnig">Sonnig</option><option value="bewölkt">Bewölkt</option>
                  <option value="regen">Regen</option><option value="schnee">Schnee</option><option value="wind">Wind</option>
                </select>
                <Input value={diaryForm.workers} onChange={e => setDiaryForm({...diaryForm, workers: e.target.value})}
                  placeholder="Arbeiter vor Ort (Anzahl/Namen)" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
              </div>
              <Textarea value={diaryForm.text} onChange={e => setDiaryForm({...diaryForm, text: e.target.value})}
                placeholder="Tagesbericht: Ausgeführte Arbeiten, Fortschritt, Besonderheiten..." rows={3}
                className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
              <div className="flex items-center gap-3">
                <Button variant="secondary" size="sm" onClick={() => diaryFileRef.current?.click()} className="bg-slate-800 text-slate-200 border border-slate-700 rounded-sm">
                  <ImageIcon className="w-4 h-4 mr-1" />{diaryPhoto ? diaryPhoto.name : 'Foto anhängen'}
                </Button>
                <input ref={diaryFileRef} type="file" accept="image/*" className="hidden" onChange={e => setDiaryPhoto(e.target.files[0])} />
                <Button onClick={handleDiarySubmit} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm ml-auto">Eintrag speichern</Button>
              </div>
            </div>
            {diary.map(d => (
              <div key={d.id} className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4" data-testid={`diary-${d.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono text-amber-400">{d.date}</span>
                    {d.weather && <Badge className="bg-slate-800 text-slate-300 border-slate-700 rounded-sm text-xs">{d.weather}</Badge>}
                    {d.workers && <span className="text-xs text-slate-500">Arbeiter: {d.workers}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">{d.created_by_name}</span>
                    <Button variant="ghost" size="icon" onClick={async () => { await axios.delete(`${API}/diary/${d.id}`); setDiary(prev => prev.filter(x => x.id !== d.id)); toast.success('Gelöscht'); }}
                      className="text-slate-500 hover:text-rose-400 w-7 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{d.text}</p>
                {d.has_photo && <img src={`${API}/diary/${d.id}/photo`} alt="" className="mt-2 rounded-sm max-h-[200px] object-cover border border-slate-800" />}
              </div>
            ))}
            {!diary.length && <p className="text-sm text-slate-500 text-center py-4">Noch keine Tagebucheinträge</p>}
          </TabsContent>

          {/* Aufmaß */}
          <TabsContent value="aufmass" className="mt-4 space-y-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 space-y-3">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2"><Ruler className="w-4 h-4 text-amber-500" />Neues Aufmaß</h3>
              <Input value={measForm.room} onChange={e => setMeasForm({...measForm, room: e.target.value})}
                placeholder="Raum / Bereich (z.B. Wohnzimmer, Fassade Nord)" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
              {measForm.items.map((item, i) => (
                <div key={i} className="grid grid-cols-[1fr_70px_70px_70px_60px_70px_28px] gap-2 items-end">
                  <div>{i===0 && <Label className="text-xs text-slate-500">Beschreibung</Label>}
                  <Input value={item.description} onChange={e => { const items = [...measForm.items]; items[i].description = e.target.value; setMeasForm({...measForm, items}); }}
                    placeholder="Wand, Boden..." className="bg-slate-950 border-slate-800 text-slate-200 text-sm placeholder:text-slate-600" /></div>
                  <div>{i===0 && <Label className="text-xs text-slate-500">Länge</Label>}
                  <Input type="number" step="0.01" value={item.length} onChange={e => { const items = [...measForm.items]; items[i].length = parseFloat(e.target.value)||0; setMeasForm({...measForm, items}); }}
                    className="bg-slate-950 border-slate-800 text-slate-200 text-sm" /></div>
                  <div>{i===0 && <Label className="text-xs text-slate-500">Breite</Label>}
                  <Input type="number" step="0.01" value={item.width} onChange={e => { const items = [...measForm.items]; items[i].width = parseFloat(e.target.value)||0; setMeasForm({...measForm, items}); }}
                    className="bg-slate-950 border-slate-800 text-slate-200 text-sm" /></div>
                  <div>{i===0 && <Label className="text-xs text-slate-500">Höhe</Label>}
                  <Input type="number" step="0.01" value={item.height} onChange={e => { const items = [...measForm.items]; items[i].height = parseFloat(e.target.value)||0; setMeasForm({...measForm, items}); }}
                    className="bg-slate-950 border-slate-800 text-slate-200 text-sm" /></div>
                  <div>{i===0 && <Label className="text-xs text-slate-500">Anz.</Label>}
                  <Input type="number" value={item.quantity} onChange={e => { const items = [...measForm.items]; items[i].quantity = parseFloat(e.target.value)||1; setMeasForm({...measForm, items}); }}
                    className="bg-slate-950 border-slate-800 text-slate-200 text-sm" /></div>
                  <div>{i===0 && <Label className="text-xs text-slate-500">Einheit</Label>}
                  <select value={item.unit} onChange={e => { const items = [...measForm.items]; items[i].unit = e.target.value; setMeasForm({...measForm, items}); }}
                    className="bg-slate-950 border border-slate-800 text-slate-200 rounded-sm text-sm h-9 px-2">
                    <option value="m²">m²</option><option value="m³">m³</option><option value="lfm">lfm</option><option value="Stk.">Stk.</option>
                  </select></div>
                  {measForm.items.length > 1 && <button onClick={() => setMeasForm({...measForm, items: measForm.items.filter((_,j)=>j!==i)})} className="text-slate-500 hover:text-rose-400 h-9 flex items-center">×</button>}
                </div>
              ))}
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setMeasForm({...measForm, items: [...measForm.items, { description: '', length: 0, width: 0, height: 0, quantity: 1, unit: 'm²', notes: '' }]})}
                  className="text-amber-400"><Plus className="w-4 h-4 mr-1" />Position</Button>
                <Button onClick={handleMeasSubmit} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">Aufmaß speichern</Button>
              </div>
            </div>
            {measurements.map(m => (
              <div key={m.id} className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4" data-testid={`meas-${m.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2"><Ruler className="w-4 h-4 text-slate-400" />{m.room}</h4>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-400/20 rounded-sm text-xs font-mono">{m.total_area} {m.items?.[0]?.unit || 'm²'}</Badge>
                    <Button variant="ghost" size="icon" onClick={async () => { await axios.delete(`${API}/measurements/${m.id}`); setMeasurements(prev => prev.filter(x => x.id !== m.id)); toast.success('Gelöscht'); }}
                      className="text-slate-500 hover:text-rose-400 w-7 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {m.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 px-2 bg-slate-950/30 rounded-sm">
                      <span className="text-slate-300">{item.description}</span>
                      <span className="font-mono text-slate-400">{item.length}×{item.width}{item.height ? `×${item.height}` : ''} × {item.quantity} = <span className="text-amber-400">{item.area} {item.unit}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!measurements.length && <p className="text-sm text-slate-500 text-center py-4">Noch keine Aufmaße erfasst</p>}
          </TabsContent>
        </Tabs>

        {/* Edit Dialog (accessible from detail view) */}
        <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) { setSelectedAngebot(null); setEditingProject(null); resetForm(); } }}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">{editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Projektname</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Beschreibung</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Kunde</Label>
                <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {customers.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-slate-300">Startdatum</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
                <div><Label className="text-slate-300">Enddatum</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
                <div><Label className="text-slate-300">Geplante Dauer (Tage)</Label><Input type="number" value={form.planned_duration_days} onChange={e => setForm({...form, planned_duration_days: e.target.value})} placeholder="z.B. 14" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
              </div>
              {editingProject && (
                <div><Label className="text-slate-300">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="aktiv" className="text-slate-200">Aktiv</SelectItem>
                    <SelectItem value="pausiert" className="text-slate-200">Pausiert</SelectItem>
                    <SelectItem value="abgeschlossen" className="text-slate-200">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select></div>
              )}
              {isAdmin && (
                <div>
                  <Label className="text-slate-300">Zugeteilte Mitarbeiter</Label>
                  <div className="mt-2 max-h-[150px] overflow-y-auto bg-slate-950/50 border border-slate-800/40 rounded-sm">
                    {allUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-800/30 hover:bg-slate-800/30 cursor-pointer">
                        <Checkbox checked={(form.members || []).includes(u.id)} onCheckedChange={checked => {
                          setForm(prev => ({ ...prev, members: checked ? [...(prev.members||[]), u.id] : (prev.members||[]).filter(id => id !== u.id) }));
                        }} className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                        <span className="text-sm text-slate-200">{u.name}</span>
                        <span className="text-[10px] text-slate-500 capitalize ml-auto">{u.role}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">{(form.members||[]).length} Mitarbeiter ausgewählt</p>
                </div>
              )}
              <Button onClick={handleCreate} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
                {editingProject ? 'Projekt speichern' : 'Projekt erstellen'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="projekte-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Projekte</h1>
        {canEditProjects && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-project-button"
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <Plus className="w-4 h-4 mr-2" />Neues Projekt
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={projectTab === 'aktiv' ? 'default' : 'secondary'}
          onClick={() => setProjectTab('aktiv')} data-testid="tab-active-projects"
          className={projectTab === 'aktiv' ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>
          Aktive Projekte <span className="ml-1.5 text-xs opacity-70">({activeProjects.length})</span>
        </Button>
        <Button size="sm" variant={projectTab === 'abgeschlossen' ? 'default' : 'secondary'}
          onClick={() => setProjectTab('abgeschlossen')} data-testid="tab-closed-projects"
          className={projectTab === 'abgeschlossen' ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>
          Abgeschlossen <span className="ml-1.5 text-xs opacity-70">({closedProjects.length})</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(projectTab === 'aktiv' ? activeProjects : closedProjects).map(p => (
          <div key={p.id} onClick={() => setSelected(p)} data-testid={`project-card-${p.id}`}
            className={`bg-slate-900/50 border backdrop-blur-md rounded-sm p-5 cursor-pointer hover:border-slate-700 transition-colors
              ${p.status === 'abgeschlossen' ? 'border-slate-800/40 opacity-80' : 'border-slate-800/60'}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-white">{p.name}</h3>
              <Badge className={`${statusColors[p.status] || ''} rounded-sm text-xs`}>{p.status}</Badge>
            </div>
            <p className="text-sm text-slate-400 line-clamp-2 mb-3">{p.description || 'Keine Beschreibung'}</p>
            <div className="text-xs font-mono text-slate-600">
              {p.start_date || '—'} → {p.end_date || '—'}
            </div>
          </div>
        ))}
        {(projectTab === 'aktiv' ? activeProjects : closedProjects).length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            {projectTab === 'aktiv' ? 'Keine aktiven Projekte' : 'Keine abgeschlossenen Projekte'}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) { setSelectedAngebot(null); setEditingProject(null); resetForm(); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}</DialogTitle></DialogHeader>
          <div className="space-y-4">

            {/* Angebot-Auswahl (nur bei Neuanlage) */}
            {!editingProject && angebote.length > 0 && (
              <div className="bg-slate-950/60 border border-amber-500/20 rounded-sm p-4">
                <Label className="text-amber-400 flex items-center gap-2 mb-2">
                  <FileCheck className="w-4 h-4" strokeWidth={1.5} />
                  Aus bestehendem Angebot erstellen
                </Label>
                <Select value={selectedAngebot?.id || 'none'} onValueChange={v => {
                  if (v === 'none') { applyAngebot(null); } else { applyAngebot(angebote.find(a => a.id === v)); }
                }}>
                  <SelectTrigger data-testid="angebot-select" className="bg-slate-950 border-slate-800 text-slate-200">
                    <SelectValue placeholder="Angebot auswählen (optional)..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">Kein Angebot — manuell erstellen</SelectItem>
                    {angebote.map(a => (
                      <SelectItem key={a.id} value={a.id} className="text-slate-200">
                        <span className="font-mono text-amber-400 mr-2">{a.doc_number}</span>
                        {customers.find(c => c.id === a.customer_id)?.name || 'Unbekannt'}
                        <span className="text-slate-500 ml-2">({a.total?.toFixed(2)} EUR)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedAngebot && (
                  <div className="mt-2 text-xs text-slate-400 bg-slate-900/50 rounded-sm p-2 border border-slate-800/40">
                    <span className="text-emerald-400 font-medium">Angebot übernommen:</span> {selectedAngebot.doc_number}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-slate-300">Projektname</Label>
              <Input data-testid="project-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Dachsanierung Müller" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Beschreibung</Label>
              <Textarea data-testid="project-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Projektdetails..." rows={3}
                className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Kunde</Label>
              <Select value={form.customer_id} onValueChange={v => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="project-customer-select" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {customers.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-300">Startdatum</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Enddatum</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Geplante Dauer (Tage)</Label>
                <Input type="number" value={form.planned_duration_days} onChange={e => setForm({...form, planned_duration_days: e.target.value})}
                  placeholder="z.B. 14" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
            </div>
            {editingProject && (
              <div>
                <Label className="text-slate-300">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="aktiv" className="text-slate-200">Aktiv</SelectItem>
                    <SelectItem value="pausiert" className="text-slate-200">Pausiert</SelectItem>
                    <SelectItem value="abgeschlossen" className="text-slate-200">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {isAdmin && (
              <div>
                <Label className="text-slate-300">Zugeteilte Mitarbeiter</Label>
                <div className="mt-2 max-h-[150px] overflow-y-auto bg-slate-950/50 border border-slate-800/40 rounded-sm">
                  {allUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-800/30 hover:bg-slate-800/30 cursor-pointer">
                      <Checkbox checked={(form.members || []).includes(u.id)} onCheckedChange={checked => {
                        setForm(prev => ({ ...prev, members: checked ? [...(prev.members||[]), u.id] : (prev.members||[]).filter(id => id !== u.id) }));
                      }} className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                      <span className="text-sm text-slate-200">{u.name}</span>
                      <span className="text-[10px] text-slate-500 capitalize ml-auto">{u.role}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{(form.members||[]).length} Mitarbeiter ausgewählt</p>
              </div>
            )}
            <Button onClick={handleCreate} data-testid="create-project-submit" className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editingProject ? 'Projekt speichern' : (selectedAngebot ? 'Projekt aus Angebot erstellen' : 'Projekt erstellen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
